import React, { useMemo } from 'react';
import { ClinicPet } from 'shared/types/graphql';
import { titleCase } from 'shared/utils';
import PetAvatar from 'shared/components/Avatars/PetAvatar';
import { HStack } from '@televet/kibble-ui/build/chakra';
import { Text, TextSize } from '@televet/kibble-ui/build/components/Text';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { Box } from '@televet/kibble-ui';
import { TypographyVariant } from '@televet/kibble-ui/build/shared/types/typography';

export type TruncatedPetListPet = Pick<ClinicPet, 'id' | 'name' | 'species' | 'speciesType' | 'computedIsDeceased'>;

interface ITruncatedPetList {
  clinicPets: TruncatedPetListPet[];
  fontWeight?: 'semibold' | 'regular';
  fontSize?: TextSize;
  fontVariant?: TypographyVariant;
}

const TruncatedPetList = ({
  clinicPets,
  fontWeight = 'semibold',
  fontSize = 'lg',
  fontVariant = 'subtle',
}: ITruncatedPetList): JSX.Element | null => {
  const displayedPet = clinicPets[0];

  const petNames = useMemo(() => {
    return clinicPets
      .slice(1)
      .map((pet) => pet.name)
      .join(', ');
  }, [clinicPets]);

  return (
    <HStack spacing={1} align="center" overflow="hidden" position="relative">
      {!!displayedPet && (
        <HStack key={displayedPet.id} spacing={1}>
          <PetAvatar speciesType={displayedPet.speciesType} isDeceased={displayedPet.computedIsDeceased} iconOnly />
          <Text whiteSpace="nowrap" size={fontSize} variant={fontVariant} fontWeight={fontWeight}>
            {titleCase(displayedPet.name)}
          </Text>
        </HStack>
      )}
      {clinicPets.length > 1 && (
        <Tooltip label={petNames} position="relative">
          <Box position="relative" display="block">
            <Text
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              variant={fontVariant}
              fontWeight={fontWeight}
              position="relative"
            >
              + {clinicPets.length - 1} more
            </Text>
          </Box>
        </Tooltip>
      )}
    </HStack>
  );
};

TruncatedPetList.displayName = 'TruncatedPetList';

export default TruncatedPetList;
